/**
 * Portal Teaching Tips API Client
 *
 * Handles operations for teaching tips bulletin board with Cognito authentication.
 */

// API Gateway URL
const API_BASE_URL = 'https://jrgfnay5w2.execute-api.ap-southeast-2.amazonaws.com/v1';

// S3 bucket URL for file downloads
const TIPS_FILE_BASE_URL = 'https://uplus-portal-teaching-tips.s3.ap-southeast-2.amazonaws.com';

/**
 * Get the authentication token from the user's session.
 */
const getAuthToken = () => {
  const oidcStorage = sessionStorage.getItem('oidc.user:https://cognito-idp.ap-southeast-2.amazonaws.com/ap-southeast-2_3n9j6gDzL:2c6na652k2lkcsaa1ol8m5chp4');
  if (oidcStorage) {
    try {
      const oidcData = JSON.parse(oidcStorage);
      return oidcData.id_token || oidcData.access_token;
    } catch (e) {
      console.error('Error parsing OIDC storage:', e);
    }
  }
  return null;
};

/**
 * Make an authenticated API request to teaching-tips endpoint.
 */
const apiRequest = async (operation, data = {}) => {
  const token = getAuthToken();

  if (!token) {
    throw new Error('Not authenticated. Please log in again.');
  }

  const response = await fetch(`${API_BASE_URL}/teaching-tips`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      operation,
      data
    })
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || `Request failed with status ${response.status}`);
  }

  return result;
};

// ==================== Teaching Tips API ====================

/**
 * List all teaching tips (sorted by pinned desc, createdAt desc).
 */
export const listTips = async () => {
  const result = await apiRequest('list', {});
  return result.tips || [];
};

/**
 * Create a new teaching tip.
 */
export const createTip = async (data) => {
  const result = await apiRequest('create', data);
  return result.tip;
};

/**
 * Update an existing teaching tip.
 */
export const updateTip = async (tipId, updates) => {
  const result = await apiRequest('update', { tipId, ...updates });
  return result.tip;
};

/**
 * Delete a teaching tip.
 */
export const deleteTip = async (tipId) => {
  return await apiRequest('delete', { tipId });
};

/**
 * Get presigned upload URLs for file attachments.
 * @param {string} tipId - The tip ID
 * @param {Array} files - Array of {fileName, contentType}
 */
export const getUploadUrls = async (tipId, files) => {
  const result = await apiRequest('getUploadUrls', { tipId, files });
  return result.uploadUrls || [];
};

/**
 * Upload a file to S3 using a presigned URL.
 */
export const uploadTipFile = async (uploadUrl, file, contentType) => {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: file
  });

  if (!response.ok) {
    throw new Error(`Failed to upload file: ${response.status}`);
  }

  return true;
};

/**
 * Get the public download URL for a tip attachment.
 */
export const getTipFileUrl = (s3Key) => `${TIPS_FILE_BASE_URL}/${s3Key}`;

// ==================== Default Export ====================

const teachingTipsApi = {
  listTips,
  createTip,
  updateTip,
  deleteTip,
  getUploadUrls,
  uploadTipFile,
  getTipFileUrl
};

export default teachingTipsApi;
